import { AlertTriangle } from 'lucide-react';

import type { UploadRejection } from './upload-errors';

export interface UploadRejectionNoticeProps {
  rejection: UploadRejection;
}

/**
 * A refused upload, rendered in three parts.
 *
 * The order is the order a person needs them in: **what went wrong** as a
 * headline they can recognise, **what to do about it** in a sentence, and only
 * then **the server's own words**, which carry the specifics — the file, the
 * figure, the limit — but are written for an archive rather than for a person
 * with a laptop open.
 *
 * The listed paths are pulled out of that message and shown as a list rather
 * than left inside a paragraph, because "which of my files was it" is the
 * question every one of these errors provokes, and a comma-separated run of
 * paths inside prose is the least readable possible answer.
 */
export const UploadRejectionNotice = ({ rejection }: UploadRejectionNoticeProps) => (
  <div
    role="alert"
    className="space-y-3 rounded-md border border-destructive/30 bg-destructive/5 p-4"
  >
    <div className="flex items-start gap-3">
      <AlertTriangle
        aria-hidden="true"
        strokeWidth={1.75}
        className="mt-0.5 size-4 shrink-0 text-destructive"
      />
      <div className="space-y-1">
        <p className="text-sm font-medium text-destructive">{rejection.title}</p>
        <p className="text-sm text-muted-foreground">{rejection.remedy}</p>
      </div>
    </div>

    {rejection.files.length === 0 ? null : (
      <ul className="max-h-32 space-y-1 overflow-y-auto rounded-sm bg-background/60 p-2 font-mono text-xs text-foreground">
        {rejection.files.map((path) => (
          <li key={path} className="truncate" title={path}>
            {path}
          </li>
        ))}
      </ul>
    )}

    {rejection.detail === null ? null : (
      <p className="border-t border-destructive/20 pt-3 text-xs text-muted-foreground">
        {rejection.detail}
      </p>
    )}
  </div>
);
